export interface IApiError {
  httpStatusCode: number;
  message: string;
  data?: Record<string, string>;
  url?: string;
  code?: string;
  availableFrom?: number;
  availableTo?: number;
  /** `httpStatusCode` from the platform VError body; may differ from the wire status. */
  bodyStatusCode?: number;
}

export class ApiError extends Error {
  readonly httpStatusCode: number;
  readonly data?: Record<string, string>;
  readonly url?: string;
  /** Structured analytics error code from the `{error:{code,...}}` envelope (ADR-003 §8). */
  readonly code?: string;
  /** UTC epoch seconds; present on coverage/product-window errors. */
  readonly availableFrom?: number;
  /** UTC epoch seconds; present on coverage errors. */
  readonly availableTo?: number;
  /** `httpStatusCode` from the platform VError body; may differ from the wire status. */
  readonly bodyStatusCode?: number;

  constructor(error: IApiError) {
    super(error.message);
    this.name = 'ApiError';
    this.httpStatusCode = error.httpStatusCode;
    this.data = error.data;
    this.url = error.url;
    this.code = error.code;
    this.availableFrom = error.availableFrom;
    this.availableTo = error.availableTo;
    this.bodyStatusCode = error.bodyStatusCode;
  }

  get isConflict(): boolean {
    return this.httpStatusCode === 409;
  }

  get isAuthError(): boolean {
    return this.httpStatusCode === 401 || this.httpStatusCode === 403;
  }

  /**
   * Platform authentication rejection: the backend's rejection handler completes
   * expired/invalid sessions as wire 403 with a VError body of httpStatusCode 401
   * ("Not authenticated.") — never wire 401 — so the wire status alone cannot
   * identify them (backend customRejectionHandler; gateway contract §Error contract).
   * Wrong-credential logins share this signature, so WebApi treats it as session
   * expiry only on requests that were sent with an auth token.
   */
  get isNotAuthenticated(): boolean {
    return this.httpStatusCode === 403 && this.bodyStatusCode === 401;
  }

  get isNotFound(): boolean {
    return this.httpStatusCode === 404;
  }

  /**
   * Guard for single-entity GET responses. Backend routes complete Scala
   * `Option` results directly, so "not found / not owned by this cId" arrives
   * as 200 with an empty body, which WebApi parses to `{}` — truthy, and it
   * slips past `!data` guards (crashes on first nested read, e.g. after a
   * sub-account switch re-fires a detail query with the new cId). Probing one
   * required top-level field converts that into a proper 404 ApiError at the
   * API boundary, so the declared response type actually holds for callers.
   */
  static requireFound<T extends object>(value: T | null | undefined, probeKey: keyof T, label: string): T {
    if (value == null || value[probeKey] === undefined) {
      throw new ApiError({ httpStatusCode: 404, message: `${label} not found` });
    }
    return value;
  }
}
